import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { Tr } from '@utils';
import { useLoggedinUser } from '@hooks';
import { EVPreference, FleetVehicle, User } from '@services';
import { IVehicleQuestionCommonProps } from '@models';
import { Button, Divider, Grid, Stack, Typography } from '@mui/material';
import { FormikRadio, FormikTextfield, VoltaageModalDialog } from '@components/common';
import { FormStepLayout, FormStepQuestion } from '@components/portal/common';

type FormFields = Partial<FleetVehicle>;
const translationPath = (key: string) => `vehicle-list.add-edit-vehicle.question-vehicle-driver.${key}`;
const getFormFieldValues = (values?: FormFields, user?: Maybe<User>): FormFields => {
	return {
		driverName: (values?.driverName ? values.driverName : user?.isDriver() ? user.name : undefined) || undefined,
		driverSurname: (values?.driverSurname ? values.driverSurname : user?.isDriver() ? user.surname : undefined) || undefined,
		driverEmail: (values?.driverEmail ? values.driverEmail : user?.isDriver() ? user.email : undefined) || undefined,
		evPreferences: values?.evPreferences
	};
};

export interface IQuestionVehicleDriverProps extends IVehicleQuestionCommonProps<FormFields> {
	onSaveAndReturn?: IVehicleQuestionCommonProps<FormFields>['onNext'];
}

export const QuestionVehicleDriver: FC<IQuestionVehicleDriverProps> = ({ values, onBack, onNext, onSaveAndReturn, ...props }) => {
	const { t } = useTranslation('portal');
	const user = useLoggedinUser();
	const [finalizeDialog, setFinalizeDialog] = useState(false);
	const [initialValues, setInitialValues] = useState<FormFields>(getFormFieldValues(values, user));
	useEffect(() => setInitialValues(getFormFieldValues(values, user)), [values, user]);

	const formik = useFormik<FormFields>({
		initialValues,
		enableReinitialize: true,
		validateOnChange: true,
		validateOnMount: true,
		validationSchema: yup.object({
			driverName: yup.string().optional().nullable(),
			driverSurname: yup.string().optional().nullable(),
			driverEmail: yup.string().optional().nullable().email()
		}),
		onSubmit: (formValues, api) => {
			onNext?.(formValues);
			api.setSubmitting(false);
		}
	});
	const { submitForm, handleSubmit } = formik;
	const disabled = !formik.isValid || formik.isSubmitting;

	return (
		<FormStepLayout
			title={<Tr.Portal path={translationPath('title')} />}
			primaryButtonProps={{ disabled, onClick: submitForm, 'data-testid': 'add-edit-vehicle-next-button' }}
			secondaryButtonProps={{ onClick: onBack, 'data-testid': 'add-edit-vehicle-back-button' }}
			{...props}
		>
			<form onSubmit={handleSubmit}>
				<Stack direction="column" spacing={4}>
					<FormStepQuestion title={<Tr.Portal path={translationPath('first-question')} />}>
						<Grid container rowSpacing={5} columnSpacing={4}>
							<Grid item xs={6}>
								<FormikTextfield
									formikApi={formik}
									name="driverName"
									type={user?.isSellerLoggedInAsManager() && initialValues.driverName ? 'password' : undefined}
									disabled={!!(user?.isSellerLoggedInAsManager() && initialValues.driverName)}
									title={t(translationPath('name'))}
									placeholder={t(translationPath('placeholder.name'))}
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikTextfield
									formikApi={formik}
									name="driverSurname"
									type={user?.isSellerLoggedInAsManager() && initialValues.driverSurname ? 'password' : undefined}
									disabled={!!(user?.isSellerLoggedInAsManager() && initialValues.driverSurname)}
									title={t(translationPath('surname'))}
									placeholder={t(translationPath('placeholder.surname'))}
								/>
							</Grid>
							<Grid item xs={6}>
								<FormikTextfield
									formikApi={formik}
									name="driverEmail"
									type={user?.isSellerLoggedInAsManager() && initialValues.driverEmail ? 'password' : undefined}
									title={t(translationPath('email'))}
									placeholder={t(translationPath('placeholder.email'))}
									disabled={
										!!(user?.isSellerLoggedInAsManager() && initialValues.driverName) ||
										!(formik.values.driverName && formik.values.driverSurname) ||
										!!values?.driverEmail
									}
									onBlur={() => {
										if (!values?.driverEmail && formik.values.driverEmail && !formik.errors.driverEmail) {
											setFinalizeDialog(true);
										}
									}}
								/>
								<VoltaageModalDialog
									open={finalizeDialog}
									onClose={(_, reason) => reason !== 'backdropClick' && setFinalizeDialog(false)}
									maxWidth="sm"
									hideCloseIcon
									disableEscapeKeyDown
								>
									<Stack direction="column" spacing={4} sx={{ padding: 3, paddingTop: 0 }}>
										<Typography sx={{ textAlign: 'center' }}>
											<Tr.Portal path={translationPath('driver-to-complete-dialog.content')} />
										</Typography>
										<Divider light />
										<Stack direction="row" alignItems="center" justifyContent="center" spacing={3}>
											<Button
												sx={{ minWidth: 120 }}
												variant="contained"
												onClick={() => onSaveAndReturn?.(formik.values)}
											>
												<Tr.Portal path="yes" />
											</Button>
											<Button
												sx={{ minWidth: 120 }}
												variant="contained"
												color="error"
												onClick={() => setFinalizeDialog(false)}
											>
												<Tr.Portal path="no" />
											</Button>
										</Stack>
									</Stack>
								</VoltaageModalDialog>
							</Grid>
						</Grid>
					</FormStepQuestion>
					<FormStepQuestion title={<Tr.Portal path={translationPath('second-question')} />}>
						<Grid container>
							<Grid item xs={4}>
								<FormikRadio
									formikApi={formik}
									name="evPreferences"
									value={EVPreference.YES}
									formControlLabelProps={{ label: <Tr.Portal path="yes" /> }}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikRadio
									formikApi={formik}
									name="evPreferences"
									value={EVPreference.NO}
									formControlLabelProps={{ label: <Tr.Portal path="no" /> }}
								/>
							</Grid>
							<Grid item xs={4}>
								<FormikRadio
									formikApi={formik}
									name="evPreferences"
									value={EVPreference.UNKNOWN}
									formControlLabelProps={{ label: <Tr.Portal path={translationPath('not-known')} /> }}
								/>
							</Grid>
						</Grid>
					</FormStepQuestion>
					<Stack direction="column" spacing={3} sx={{ paddingTop: 5 }}>
						<Divider light />
						<Typography variant="body2" sx={{ color: 'grey.800' }}>
							{t(translationPath('footnote'))}
						</Typography>
					</Stack>
				</Stack>
			</form>
		</FormStepLayout>
	);
};
